import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Badge, Flight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export type NationalIdType = 'CEDULA' | 'PASSPORT';

interface NationalIdInputProps {
  nationalIdType: NationalIdType;
  nationalId: string;
  passportCountry?: string;
  onNationalIdTypeChange: (type: NationalIdType) => void;
  onNationalIdChange: (value: string) => void;
  onPassportCountryChange: (country: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

// Common passport countries for Dominican Republic context
const PASSPORT_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'ES', name: 'Spain' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'MX', name: 'Mexico' },
  { code: 'CO', name: 'Colombia' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'HT', name: 'Haiti' },
];

/**
 * Format cedula with dashes: ###-#######-#
 */
const formatCedula = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10)}`;
};

/**
 * Validate cedula using LUHN algorithm
 */
const isValidCedula = (cedula: string): boolean => {
  const digits = cedula.replace(/\D/g, '');
  if (digits.length !== 11) return false;

  const weights = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1];
  let sum = 0;

  for (let i = 0; i < 11; i++) {
    let digit = parseInt(digits[i]!, 10) * weights[i]!;
    if (digit >= 10) digit -= 9;
    sum += digit;
  }

  return sum % 10 === 0;
};

export const NationalIdInput: React.FC<NationalIdInputProps> = ({
  nationalIdType,
  nationalId,
  passportCountry = '',
  onNationalIdTypeChange,
  onNationalIdChange,
  onPassportCountryChange,
  error = false,
  helperText,
  required = false,
  disabled = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [displayValue, setDisplayValue] = useState(nationalId);

  // Format cedula on display
  useEffect(() => {
    if (nationalIdType === 'CEDULA') {
      setDisplayValue(formatCedula(nationalId));
    } else {
      setDisplayValue(nationalId.toUpperCase());
    }
  }, [nationalId, nationalIdType]);

  const handleTypeChange = (_: React.MouseEvent<HTMLElement>, newType: NationalIdType | null) => {
    if (newType) {
      onNationalIdTypeChange(newType);
      onNationalIdChange(''); // Clear value when type changes
      if (newType === 'CEDULA') {
        onPassportCountryChange('');
      }
    }
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (nationalIdType === 'CEDULA') {
      // Only allow digits and dashes for cedula
      const digits = value.replace(/\D/g, '').slice(0, 11);
      setDisplayValue(formatCedula(digits));
      onNationalIdChange(digits);
    } else {
      // Passport: uppercase alphanumeric
      const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
      setDisplayValue(cleaned);
      onNationalIdChange(cleaned);
    }
  };

  // Determine validation state for cedula
  const isCedulaComplete = nationalIdType === 'CEDULA' && nationalId.length === 11;
  const isCedulaValid = isCedulaComplete && isValidCedula(nationalId);
  const showCedulaError = isCedulaComplete && !isCedulaValid;

  return (
    <Box>
      {/* Type selector */}
      <ToggleButtonGroup
        value={nationalIdType}
        exclusive
        onChange={handleTypeChange}
        size="small"
        sx={{ mb: 1.5 }}
        disabled={disabled}
      >
        <ToggleButton value="CEDULA" sx={{ px: 2 }}>
          <Badge sx={{ mr: 0.5, fontSize: 18 }} />
          {t('patient.cedula', 'Cedula')}
        </ToggleButton>
        <ToggleButton value="PASSPORT" sx={{ px: 2 }}>
          <Flight sx={{ mr: 0.5, fontSize: 18 }} />
          {t('patient.passport', 'Passport')}
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Passport country selector (only for passport) */}
      {nationalIdType === 'PASSPORT' && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }} disabled={disabled}>
          <InputLabel>{t('patient.passportCountry', 'Issuing Country')} *</InputLabel>
          <Select
            value={passportCountry}
            onChange={(e) => onPassportCountryChange(e.target.value)}
            label={`${t('patient.passportCountry', 'Issuing Country')} *`}
          >
            {PASSPORT_COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* National ID input */}
      <TextField
        fullWidth
        label={
          nationalIdType === 'CEDULA'
            ? t('patient.cedulaNumber', 'Cedula Number')
            : t('patient.passportNumber', 'Passport Number')
        }
        value={displayValue}
        onChange={handleIdChange}
        error={error || showCedulaError}
        helperText={
          showCedulaError
            ? t('patient.invalidCedula', 'Invalid cedula number')
            : helperText
        }
        required={required}
        disabled={disabled}
        placeholder={nationalIdType === 'CEDULA' ? '000-0000000-0' : 'ABC123456'}
        InputProps={{
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{
          maxLength: nationalIdType === 'CEDULA' ? 13 : 15, // 11 digits + 2 dashes for cedula
        }}
      />

      {/* Cedula format hint */}
      {nationalIdType === 'CEDULA' && nationalId.length > 0 && nationalId.length < 11 && (
        <FormHelperText>
          {t('patient.cedulaHint', 'Enter 11 digits (format: 000-0000000-0)')}
        </FormHelperText>
      )}
    </Box>
  );
};
